"use client";

import { useState, useEffect, FormEvent } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { User } from '@supabase/auth-js/src/lib/types';
import { AlertCircle } from "lucide-react";
import { createClient } from '@/utils/supabase/client';
import AuthLayout from '../components/AuthLayout';
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const router = useRouter();
  const supabase = createClient();

  useEffect(() => {
    const setSession = async (access_token: string, refresh_token: string) => {
      const { data, error } = await supabase.auth.setSession({
        access_token,
        refresh_token
      });
      console.log("[Login] Session:", data, error);
      fetchUser();
    };

    const fetchUser = async () => {
      const { data } = await supabase.auth.getUser();
      setUser(data?.user);
    };

    const params = new URLSearchParams(window.location.hash.substring(1));
    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');

    if (access_token && refresh_token) {
      setSession(access_token, refresh_token);
    } else {
      fetchUser();
    }
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setShowAlert(false);
    setIsLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: email, password })
      });

      const response = await res.json();

      if (response.status !== "ok") {
        setShowAlert(true);
      } else {
        router.push("/locations");
      }
    } catch (error) {
      console.error("Login error:", error);
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (user) {
    router.push("/assistant");
    return null;
  }

  return (
    <AuthLayout>
      <div className="flex-1 flex flex-col justify-center gap-8 w-full">
        {/* Title */}
        <h1 className="font-presto text-d1 text-black-800">
          Login to Urbis
        </h1>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-6 w-full">
          {showAlert && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <span>Invalid email or password!</span>
            </Alert>
          )}

          {/* Email Field */}
          <div className="flex flex-col gap-1">
            <Label htmlFor="email" className="text-small2 text-black-800 font-sans sr-only">
              Email
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-[42px] border-[#cfcac5]"
            />
          </div>

          {/* Password Field with Forgot Password Link */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="password" className="text-small2 text-black-800 font-sans sr-only">
              Password
            </Label>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-[42px] border-[#cfcac5]"
            />
            {/* Forgot Password Link */}
            <Link 
              href="/auth/forgot-password"
              className="text-[14px] leading-[24px] font-medium text-black-700 hover:underline font-sans w-fit"
            >
              Forgot Password?
            </Link>
          </div>

          {/* Submit Button - Right Aligned */}
          <div className="flex justify-end w-full mt-2">
            <Button 
              variant="primary"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Log In"}
            </Button>
          </div>
        </form>
      </div>

      {/* Sign Up Link - Bottom */}
      <div className="mt-auto flex items-center justify-center gap-1 text-[14px] leading-[24px] font-sans w-full pb-4">
        <span className="text-black-800">Don't have an account? </span>
        <Link 
          href="https://www.urbis.ai"
          className="text-black-800 font-medium hover:underline"
        >
          Sign Up
        </Link>
      </div>
    </AuthLayout>
  );
}
